import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useReadTracker, trackEvent } from '../lib/analytics';
import { Clock, ArrowLeft } from 'lucide-react';
import { AffiliateStrip } from '../components/AffiliateStrip';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/magazine/Breadcrumbs';
import AuthorBlock from '../components/magazine/AuthorBlock';
import ArticleCard from '../components/magazine/ArticleCard';
import ContinueExploring from '../components/ContinueExploring';
import SocialShare from '../components/magazine/SocialShare';
import FlagshipCard from '../components/FlagshipCard';
import { getArticle, listArticles, formatPublishedAt, renderBody, inlineFormat } from '../lib/magazineApi';

/*
  ArticlePage — single article reading surface.

  Editorial layout: max-w 720px reading column, generous leading, serif-friendly
  but staying within the Kitxens sans system for brand coherence. Cover image
  is full-bleed within the column. SEO + Open Graph fully populated.
*/

function InlineText({ text }) {
  return inlineFormat(text).map((part, i) => {
    if (part.b) {
      return (
        <strong key={i} className="font-semibold text-neutral-900">
          {part.t}
        </strong>
      );
    }
    if (part.i) {
      return (
        <em key={i} className="italic text-neutral-700">
          {part.t}
        </em>
      );
    }
    return <React.Fragment key={i}>{part.t}</React.Fragment>;
  });
}

function Paragraph({ text, lead }) {
  return (
    <p className="text-[17px] sm:text-[18px] leading-[1.75] text-neutral-700 mb-6">
      {lead && <strong className="block mb-1 text-neutral-900 font-bold tracking-[-0.01em]">{lead}</strong>}
      <InlineText text={text} />
    </p>
  );
}

function ArticleBody({ blocks }) {
  return blocks.map((b) => {
    if (b.type === 'h2') {
      return (
        <h2
          key={b.key}
          className="mt-12 mb-5 text-[24px] sm:text-[28px] font-bold text-neutral-900 tracking-[-0.015em] leading-tight"
        >
          {b.text}
        </h2>
      );
    }
    if (b.type === 'h3') {
      return (
        <h3
          key={b.key}
          className="mt-9 mb-4 text-[19px] sm:text-[21px] font-bold text-neutral-900 tracking-[-0.01em] leading-snug"
        >
          {b.text}
        </h3>
      );
    }
    if (b.type === 'hr') {
      return (
        <hr
          key={b.key}
          className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-neutral-300 to-transparent"
        />
      );
    }
    if (b.type === 'ul') {
      return (
        <ul key={b.key} className="mb-7 space-y-2.5">
          {b.items.map((item, i) => (
            <li key={i} className="pl-5 relative text-[17px] sm:text-[18px] leading-[1.7] text-neutral-700">
              <span
                aria-hidden="true"
                className="absolute left-0 top-[12px] w-2 h-2 rounded-full bg-[#F77F11]"
              />
              <InlineText text={item} />
            </li>
          ))}
        </ul>
      );
    }
    if (b.type === 'ol') {
      return (
        <ol key={b.key} className="mb-7 space-y-3 counter-reset-[ol-item]">
          {b.items.map((item, i) => (
            <li
              key={i}
              className="pl-10 relative text-[17px] sm:text-[18px] leading-[1.7] text-neutral-700"
            >
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 w-7 h-7 rounded-full bg-[#F77F11]/10 text-[#F77F11] text-[13px] font-bold flex items-center justify-center"
              >
                {i + 1}
              </span>
              <InlineText text={item} />
            </li>
          ))}
        </ol>
      );
    }
    return <Paragraph key={b.key} text={b.text} lead={b.lead} />;
  });
}

export default function ArticlePage() {
  const { slug } = useParams();
  useReadTracker(slug);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null });

  useEffect(() => {
    setLoading(true);
    setError(false);
    getArticle(slug)
      .then((a) => setArticle(a))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    listArticles({ page_size: 100 })
      .then((d) => {
        const items = d.items || [];
        const i = items.findIndex((a) => a.slug === slug);
        // items are newest-first: "next" = newer article, "prev" = older
        setNeighbors({ prev: i >= 0 ? items[i + 1] || null : null, next: i > 0 ? items[i - 1] : null });
      })
      .catch(() => setNeighbors({ prev: null, next: null }));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24">
        <p className="text-center text-neutral-500">Loading article…</p>
      </main>
    );
  }
  if (error || !article) {
    return (
      <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24">
        <div className="max-w-xl mx-auto text-center px-6">
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">404</p>
          <h1 className="mt-3 text-3xl font-bold text-neutral-900">Article not found</h1>
          <p className="mt-3 text-neutral-600">The piece you are looking for may have moved.</p>
          <Link
            to="/magazine"
            className="inline-flex items-center gap-1 mt-6 text-[#F77F11] hover:text-[#d8651a] font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Magazine
          </Link>
        </div>
      </main>
    );
  }

  const blocks = renderBody(article.body);
  const url = `/magazine/${article.slug}`;

  return (
    <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24" data-testid="magazine-article-page">
      <SEO
        path={url}
        title={article.seo_title || `${article.title} | Kitxens Magazine`}
        description={article.seo_description || article.excerpt}
        image={article.seo_og_image || article.cover_image}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.seo_description || article.excerpt,
            image: article.seo_og_image || article.cover_image,
            datePublished: article.published_at,
            author: article.author
              ? { '@type': 'Person', name: article.author.name }
              : { '@type': 'Organization', name: 'Kitxens' },
            publisher: {
              '@type': 'Organization',
              name: 'Kitxens',
              logo: { '@type': 'ImageObject', url: 'https://www.kitxens.com/kitxens-logo.png' },
            },
            mainEntityOfPage: `https://www.kitxens.com${url}`,
          },
          ...(article.faq?.length
            ? [{
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: article.faq.map((f) => ({
                  '@type': 'Question',
                  name: f.question,
                  acceptedAnswer: { '@type': 'Answer', text: f.answer },
                })),
              }]
            : []),
        ]}
      />

      {/* Breadcrumbs */}
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-2 pb-6">
        <Breadcrumbs
          items={[
            { label: 'Magazine', href: '/magazine' },
            article.category && {
              label: article.category.name,
              href: `/magazine/category/${article.category.slug}`,
            },
            { label: article.title },
          ].filter(Boolean)}
        />
      </div>

      {/* Header column */}
      <article className="max-w-[760px] mx-auto px-6 lg:px-0">
        <header>
          {article.category && (
            <Link
              to={`/magazine/category/${article.category.slug}`}
              className="inline-block text-[10.5px] tracking-[0.18em] font-bold uppercase hover:opacity-80 transition-opacity"
              style={{ color: article.category.accent_color || '#F77F11' }}
              data-testid="magazine-article-category"
            >
              {article.category.name}
            </Link>
          )}
          <h1
            className="mt-3 text-[34px] sm:text-[40px] lg:text-[48px] font-bold text-neutral-900 tracking-[-0.022em] leading-[1.08]"
            data-testid="magazine-article-title"
          >
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="mt-5 text-[18px] sm:text-[20px] text-neutral-600 leading-relaxed">
              {article.excerpt}
            </p>
          )}

          {/* Meta row */}
          <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-3">
            {article.author && (
              <AuthorBlock author={article.author} compact />
            )}
            <span className="w-1 h-1 rounded-full bg-neutral-300" />
            <span className="text-[13px] text-neutral-500">{formatPublishedAt(article.published_at)}</span>
            <span className="w-1 h-1 rounded-full bg-neutral-300" />
            <span className="inline-flex items-center gap-1 text-[13px] text-neutral-500">
              <Clock className="w-3.5 h-3.5" />
              {article.reading_time} min read
            </span>
            <div className="ml-auto">
              <SocialShare url={url} title={article.title} excerpt={article.excerpt} />
            </div>
          </div>
        </header>

        {/* Cover — object-position driven by the backend `cover_focus` field
            (top | center | bottom | left | right). Editors can tune the focal
            point per-article from the DB without touching code. Default: top,
            which keeps faces and hero focal points intact for portrait shots. */}
        {(() => {
          const cover = article.cover_image
            || 'https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1600&q=80';
          return (
          <div className="mt-10 rounded-3xl overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-50 aspect-[16/9]">
            <img
              src={cover}
              alt={article.cover_alt || article.title}
              style={{ objectPosition: article.cover_focus || 'top' }}
              className="w-full h-full object-cover"
            />
          </div>
          );
        })()}

        {/* Body */}
        <div className="mt-12" data-testid="magazine-article-body"
          onClick={(e) => {
            const a = e.target.closest && e.target.closest("a[href*='marblism.com']");
            if (a) trackEvent('partner_click', { kind: 'marblism', slug: article.slug, url: a.href });
          }}>
          {article.body_html
            ? <div className="article-prose" dangerouslySetInnerHTML={{ __html: article.body_html }} />
            : <ArticleBody blocks={blocks} />}
        </div>

        {/* FAQ (from Penny's Publication Package) */}
        {article.faq?.length > 0 && (
          <section className="mt-12 pt-8 border-t border-neutral-200" data-testid="magazine-article-faq">
            <h2 className="text-[22px] font-bold text-neutral-900 tracking-tight mb-5">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faq.map((f, i) => (
                <details key={i} className="group rounded-xl border border-neutral-200 bg-white px-5 py-4">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-semibold text-[15px] text-neutral-900">
                    {f.question}
                    <span className="text-[#F77F11] transition-transform group-open:rotate-45 shrink-0 text-lg leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-[14.5px] text-neutral-600 leading-relaxed">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Contextual flagship micro-promotion */}
        <FlagshipCard context={article.category?.slug || article.slug} className="mt-12" />

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-neutral-200 flex flex-wrap gap-2">
            {article.tags.map((tg) => (
              <span
                key={tg}
                className="inline-block text-[11px] tracking-[0.04em] uppercase font-semibold text-neutral-500 bg-white border border-neutral-200 rounded-full px-3 py-1"
              >
                {tg}
              </span>
            ))}
          </div>
        )}

        {/* Author block at the end */}
        {article.author && (
          <div className="mt-10">
            <AuthorBlock author={article.author} />
          </div>
        )}

        {/* Share footer */}
        <div className="mt-10 flex items-center justify-between">
          <Link
            to="/magazine"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-neutral-500 hover:text-[#F77F11] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Magazine
          </Link>
          <SocialShare url={url} title={article.title} excerpt={article.excerpt} />
        </div>

        {/* Previous / Next */}
        {(neighbors.prev || neighbors.next) && (
          <nav className="mt-8 grid sm:grid-cols-2 gap-4" aria-label="Article navigation" data-testid="article-prev-next">
            {neighbors.prev ? (
              <Link to={`/magazine/${neighbors.prev.slug}`} className="group rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#F77F11] transition-colors" data-testid="article-prev">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">← Previous</span>
                <p className="mt-1.5 font-semibold text-[14.5px] text-neutral-900 group-hover:text-[#F77F11] transition-colors line-clamp-2">{neighbors.prev.title}</p>
              </Link>
            ) : <span />}
            {neighbors.next && (
              <Link to={`/magazine/${neighbors.next.slug}`} className="group rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#F77F11] transition-colors sm:text-right" data-testid="article-next">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Next →</span>
                <p className="mt-1.5 font-semibold text-[14.5px] text-neutral-900 group-hover:text-[#F77F11] transition-colors line-clamp-2">{neighbors.next.title}</p>
              </Link>
            )}
          </nav>
        )}
      </article>

      {/* Related */}
      {article.related?.length > 0 && (
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 mt-20" data-testid="magazine-related">
          <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F77F11] mb-6">
            Keep reading
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {article.related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </section>
      )}

      {article && <AffiliateStrip className="mt-16 mb-16" />}
      {article && <ContinueExploring context={article.category?.slug || article.slug} />}
    </main>
  );
}
